import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import './_shimmer.scss';

/**
 * Shimmer (skeleton) loading placeholders, shared by every page.
 *
 *   <ShimmerBlock width="40%" height={20} />   one grey bar with the sweep
 *   <ShimmerPage />                             a page-shaped placeholder:
 *                                               title, a row of cards, a list
 *
 * The page preloaders (PreloaderTwo, ...) render ShimmerPage, so pages that
 * show a preloader while loading get this without changes.
 */
export function ShimmerBlock({ width = "100%", height = 14, radius = 6, circle = false, className = "", style = {} }) {
    return (
        <span
            className={"shimmer-block " + className}
            style={{
                width: circle ? height : width,
                height,
                borderRadius: circle ? "50%" : radius,
                ...style,
            }}
        />
    );
}

function ShimmerCard() {
    return (
        <div className="shimmer-card">
            <div className="d-flex align-items-center mb-3">
                <ShimmerBlock circle height={44} />
                <div className="flex-grow-1 ml-3">
                    <ShimmerBlock width="60%" height={14} className="mb-2" />
                    <ShimmerBlock width="40%" height={10} />
                </div>
            </div>
            <ShimmerBlock height={8} className="mb-2" />
            <ShimmerBlock width="80%" height={8} />
        </div>
    );
}

function ShimmerRow() {
    return (
        <div className="shimmer-row">
            <ShimmerBlock circle height={36} />
            <div className="flex-grow-1 ml-3">
                <ShimmerBlock width="45%" height={12} className="mb-2" />
                <ShimmerBlock width="70%" height={10} />
            </div>
            <ShimmerBlock width={70} height={28} radius={14} />
        </div>
    );
}

/** A section of a page still loading: a few list rows. */
export function ShimmerList({ rows = 4 }) {
    return (
        <div className="shimmer-list" role="status" aria-busy="true" aria-label="Loading">
            {Array.from({ length: rows }).map((_, i) => <ShimmerRow key={i} />)}
        </div>
    );
}

/** Table rows for a <tbody> still loading. */
export function ShimmerTableRows({ rows = 4, cols = 4 }) {
    return Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="shimmer-tr" aria-busy="true">
            {Array.from({ length: cols }).map((__, c) => (
                <td key={c}><ShimmerBlock width={c === 0 ? "80%" : "55%"} height={12} /></td>
            ))}
        </tr>
    ));
}

export function ShimmerPage({ cards = 3, rows = 5 }) {
    return (
        <div className="shimmer-page" role="status" aria-busy="true" aria-label="Loading">
            <Container>
                <ShimmerBlock width="28%" height={26} className="mb-2" />
                <ShimmerBlock width="45%" height={12} className="mb-4" />
                <Row>
                    {Array.from({ length: cards }).map((_, i) => (
                        <Col key={i} lg={Math.max(3, Math.floor(12 / cards))} md={6} xs={12} className="mb-3">
                            <ShimmerCard />
                        </Col>
                    ))}
                </Row>
                <div className="shimmer-panel">
                    <ShimmerBlock width="22%" height={16} className="mb-3" />
                    {Array.from({ length: rows }).map((_, i) => <ShimmerRow key={i} />)}
                </div>
            </Container>
        </div>
    );
}

export default ShimmerPage;
